import type { DatasetExport } from './enums';

/**
 * Catalogo delle colonne dell'export avanzato. Lo leggono il dialog (per
 * disegnare le caselle), l'API (per validare la richiesta) e il worker (per
 * produrre il file): una colonna esiste solo se è qui. `base` segna le colonne
 * dell'export CSV immediato, che la scorciatoia «Base» riseleziona.
 */
export interface ColonnaExport {
  chiave: string;
  etichetta: string;
  gruppo: string;
  base?: boolean;
  /** Come va scritta in Excel: data, data e ora, importo in euro, numero. */
  tipo?: 'data' | 'dataOra' | 'euro' | 'numero';
}

/** Foglio aggiuntivo con i dati collegati a ogni riga del foglio principale. */
export interface FoglioExport {
  chiave: string;
  etichetta: string;
}

export interface CatalogoExport {
  /** Nome del dataset al plurale, come titolo del dialog e del foglio principale. */
  etichetta: string;
  colonne: readonly ColonnaExport[];
  fogli: readonly FoglioExport[];
}

const sedeLegale = (base: boolean): ColonnaExport[] => [
  { chiave: 'indirizzo', etichetta: 'Indirizzo', gruppo: 'Sede legale' },
  { chiave: 'cap', etichetta: 'CAP', gruppo: 'Sede legale' },
  { chiave: 'comune', etichetta: 'Comune', gruppo: 'Sede legale', base },
  { chiave: 'provincia', etichetta: 'Provincia', gruppo: 'Sede legale', base },
  { chiave: 'regione', etichetta: 'Regione', gruppo: 'Sede legale' },
];

export const CATALOGO_EXPORT: Record<DatasetExport, CatalogoExport> = {
  clienti: {
    etichetta: 'Clienti',
    colonne: [
      { chiave: 'ragioneSociale', etichetta: 'Ragione sociale', gruppo: 'Anagrafica', base: true },
      {
        chiave: 'nomeCommerciale',
        etichetta: 'Nome commerciale',
        gruppo: 'Anagrafica',
        base: true,
      },
      { chiave: 'codiceFiscale', etichetta: 'Codice fiscale', gruppo: 'Anagrafica', base: true },
      { chiave: 'partitaIva', etichetta: 'Partita IVA', gruppo: 'Anagrafica', base: true },
      { chiave: 'tipologia', etichetta: 'Forma giuridica', gruppo: 'Anagrafica', base: true },
      { chiave: 'codiceAteco', etichetta: 'Codice ATECO', gruppo: 'Anagrafica' },
      ...sedeLegale(true),
      { chiave: 'email', etichetta: 'Email', gruppo: 'Contatti' },
      { chiave: 'pec', etichetta: 'PEC', gruppo: 'Contatti' },
      { chiave: 'telefono', etichetta: 'Telefono', gruppo: 'Contatti' },
      { chiave: 'codiceSdi', etichetta: 'Codice SDI', gruppo: 'Contatti' },
      { chiave: 'stato', etichetta: 'Stato', gruppo: 'Relazione', base: true },
      {
        chiave: 'canaleAcquisizione',
        etichetta: 'Canale di acquisizione',
        gruppo: 'Relazione',
        base: true,
      },
      { chiave: 'segnalatore', etichetta: 'Segnalatore', gruppo: 'Relazione' },
      { chiave: 'referenti', etichetta: 'Referenti', gruppo: 'Relazione', base: true },
      {
        chiave: 'progettiAperti',
        etichetta: 'Progetti aperti',
        gruppo: 'Relazione',
        tipo: 'numero',
      },
      { chiave: 'creatoIl', etichetta: 'Creato il', gruppo: 'Relazione', base: true, tipo: 'data' },
    ],
    fogli: [
      { chiave: 'persone', etichetta: 'Persone' },
      { chiave: 'sedi', etichetta: 'Sedi operative' },
      { chiave: 'documenti', etichetta: 'Documenti' },
    ],
  },
  partner: {
    etichetta: 'Partner',
    colonne: [
      { chiave: 'ragioneSociale', etichetta: 'Ragione sociale', gruppo: 'Anagrafica', base: true },
      { chiave: 'tipo', etichetta: 'Tipo', gruppo: 'Anagrafica', base: true },
      { chiave: 'codiceFiscale', etichetta: 'Codice fiscale', gruppo: 'Anagrafica', base: true },
      { chiave: 'partitaIva', etichetta: 'Partita IVA', gruppo: 'Anagrafica', base: true },
      { chiave: 'codiceAteco', etichetta: 'Codice ATECO', gruppo: 'Anagrafica' },
      ...sedeLegale(true),
      { chiave: 'email', etichetta: 'Email', gruppo: 'Contatti' },
      { chiave: 'pec', etichetta: 'PEC', gruppo: 'Contatti' },
      { chiave: 'telefono', etichetta: 'Telefono', gruppo: 'Contatti' },
      { chiave: 'persone', etichetta: 'Persone', gruppo: 'Relazione', base: true, tipo: 'numero' },
      {
        chiave: 'clientiSegnalati',
        etichetta: 'Clienti segnalati',
        gruppo: 'Relazione',
        tipo: 'numero',
      },
      { chiave: 'creatoIl', etichetta: 'Creato il', gruppo: 'Relazione', base: true, tipo: 'data' },
    ],
    fogli: [],
  },
  progetti: {
    etichetta: 'Progetti',
    colonne: [
      { chiave: 'codiceInterno', etichetta: 'Codice', gruppo: 'Pratica', base: true },
      { chiave: 'nome', etichetta: 'Nome', gruppo: 'Pratica', base: true },
      { chiave: 'cliente', etichetta: 'Cliente', gruppo: 'Pratica', base: true },
      { chiave: 'tipologiaPratica', etichetta: 'Tipologia', gruppo: 'Pratica', base: true },
      { chiave: 'stato', etichetta: 'Stato', gruppo: 'Pratica', base: true },
      { chiave: 'templateOrigine', etichetta: "Template d'origine", gruppo: 'Pratica' },
      { chiave: 'creatoIl', etichetta: 'Creato il', gruppo: 'Pratica', base: true, tipo: 'data' },
      {
        chiave: 'valoreInvestimento',
        etichetta: 'Valore teorico investimento',
        gruppo: 'Valori',
        tipo: 'euro',
      },
      {
        chiave: 'valoreAgevolazione',
        etichetta: 'Valore teorico agevolazione',
        gruppo: 'Valori',
        tipo: 'euro',
      },
      { chiave: 'valorePratica', etichetta: 'Valore pratica', gruppo: 'Valori', tipo: 'euro' },
      { chiave: 'fasi', etichetta: 'Fasi', gruppo: 'Avanzamento', tipo: 'numero' },
      { chiave: 'fasiAperte', etichetta: 'Fasi aperte', gruppo: 'Avanzamento', tipo: 'numero' },
      {
        chiave: 'prossimaScadenza',
        etichetta: 'Prossima scadenza',
        gruppo: 'Avanzamento',
        tipo: 'data',
      },
      { chiave: 'team', etichetta: 'Team', gruppo: 'Persone' },
      { chiave: 'personeCliente', etichetta: 'Persone del cliente', gruppo: 'Persone' },
      { chiave: 'riferimentiEsterni', etichetta: 'Riferimenti esterni', gruppo: 'Persone' },
    ],
    fogli: [],
  },
  task: {
    etichetta: 'Task',
    colonne: [
      { chiave: 'titolo', etichetta: 'Task', gruppo: 'Attività', base: true },
      { chiave: 'descrizione', etichetta: 'Descrizione', gruppo: 'Attività' },
      { chiave: 'priorita', etichetta: 'Priorità', gruppo: 'Attività', base: true },
      { chiave: 'stato', etichetta: 'Stato', gruppo: 'Attività', base: true },
      { chiave: 'assegnatario', etichetta: 'Assegnatario', gruppo: 'Collegamenti', base: true },
      { chiave: 'progetto', etichetta: 'Progetto', gruppo: 'Collegamenti', base: true },
      { chiave: 'fase', etichetta: 'Fase', gruppo: 'Collegamenti', base: true },
      { chiave: 'dataInizio', etichetta: 'Inizio', gruppo: 'Date', base: true, tipo: 'dataOra' },
      {
        chiave: 'dataScadenza',
        etichetta: 'Scadenza',
        gruppo: 'Date',
        base: true,
        tipo: 'dataOra',
      },
      {
        chiave: 'completatoIl',
        etichetta: 'Completato il',
        gruppo: 'Date',
        base: true,
        tipo: 'data',
      },
      { chiave: 'creatoIl', etichetta: 'Creato il', gruppo: 'Date', tipo: 'data' },
      { chiave: 'creatoDa', etichetta: 'Creato da', gruppo: 'Date' },
    ],
    fogli: [],
  },
  utenti: {
    etichetta: 'Utenti',
    colonne: [
      { chiave: 'nome', etichetta: 'Nome', gruppo: 'Anagrafica', base: true },
      { chiave: 'cognome', etichetta: 'Cognome', gruppo: 'Anagrafica', base: true },
      { chiave: 'email', etichetta: 'Email', gruppo: 'Anagrafica', base: true },
      { chiave: 'telefono', etichetta: 'Telefono', gruppo: 'Anagrafica' },
      { chiave: 'ruolo', etichetta: 'Ruolo', gruppo: 'Accesso', base: true },
      { chiave: 'privilegiExtra', etichetta: 'Privilegi extra', gruppo: 'Accesso', base: true },
      { chiave: 'duefa', etichetta: '2FA', gruppo: 'Accesso', base: true },
      {
        chiave: 'ultimoAccesso',
        etichetta: 'Ultimo accesso',
        gruppo: 'Accesso',
        base: true,
        tipo: 'dataOra',
      },
      { chiave: 'stato', etichetta: 'Stato', gruppo: 'Accesso', base: true },
      { chiave: 'creatoIl', etichetta: 'Creato il', gruppo: 'Accesso', base: true, tipo: 'data' },
    ],
    fogli: [],
  },
};

/** Le chiavi delle colonne Base di un dataset, nell'ordine del catalogo. */
export function colonneBase(dataset: DatasetExport): string[] {
  return CATALOGO_EXPORT[dataset].colonne.filter((c) => c.base).map((c) => c.chiave);
}
